import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext';
import { PORTAL_ACCESS } from './contexts/portalAccess';
import { signOut } from 'firebase/auth';
import { auth } from './firebase.config';
import CompanyFrontPage from './CompanyFrontPage';
import AdminDashboard from './AdminDashboard/AdminDashboard'
import Login from './AdminDashboard/cutsomerUtils/Login';
import FinishSignUp from './AdminDashboard/cutsomerUtils/FinishSignUp';
import PasswordSaved from './AdminDashboard/cutsomerUtils/PasswordSaved';
import PrivacyPolicy from './legal/PrivacyPolicy';
import TermsOfService from './legal/TermsOfService';

// /admin mounts only for a verified super-admin (root users/{uid}.role ===
// 'admin', resolved by AuthContext). A signed-in customer/employee account is
// signed out by the context and sent back to /login with an explanation; an
// unverifiable role (offline, outage) shows a retry screen and never the
// dashboard. (2026-09-07: a SimpleOffice customer could previously log into
// the empty portal shell - every action was refused server-side, but the
// shell itself should not open.)
const ProtectedRoute = ({ children }) => {
  const { user, loading, access, accessChecked } = useAuth();

  if (loading || (user && !accessChecked)) {
    return <div>Loading...</div>;
  }

  if (!user || access === PORTAL_ACCESS.DENIED || access === PORTAL_ACCESS.PENDING) {
    // the context is signing this session out; /login explains why
    return <Navigate to="/login" replace />;
  }

  if (access !== PORTAL_ACCESS.ADMIN) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[linear-gradient(160deg,#eef0f3,#c6cdd6)] p-4">
        <div className="w-full max-w-sm rounded-xl border border-[rgba(150,165,180,.25)] bg-white p-6 text-center shadow-[0_1px_3px_rgba(29,43,51,.08)]">
          <p className="mb-2 font-semibold text-[#1d2b33]">We could not verify your access to this portal.</p>
          <p className="mb-4 text-sm text-[#5b6d7a]">Check your connection and try again.</p>
          <button type="button" onClick={() => window.location.reload()} className="mb-2 w-full rounded-lg bg-[#2f66b8] p-2 text-white hover:bg-[#285aa5]">Retry</button>
          <button type="button" onClick={() => signOut(auth).catch(() => {})} className="w-full rounded-lg border border-[rgba(150,165,180,.4)] bg-white p-2 text-[#1d2b33] hover:bg-[#f4f7fa]">Sign out</button>
        </div>
      </div>
    );
  }

  return children;
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<CompanyFrontPage />} />
        {/* Public legal pages (2026-10-07): linked from the front-page footer,
            the apps, and the Brevo SMS toll-free registration (the privacy
            page carries Brevo's required clause verbatim). */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/login" element={<Login />} />
        {/* Registration-email landing pages — createCustomer/createUser links
            point here (adminDashboardF actionCodeSettings). Missing routes
            rendered a blank page and blocked new-customer signup. */}
        <Route path="/finishSignUp" element={<FinishSignUp flow="customer" />} />
        <Route path="/finishUserSignUp" element={<FinishSignUp flow="user" />} />
        {/* Continue URL of every password-reset flow (Firebase's hosted widget
            saves the password first, then sends people here). Was unrouted -
            the "Continue" button used to land on a blank page. */}
        <Route path="/resetPassword" element={<PasswordSaved />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        {/* /__/auth/action is served by Firebase Hosting's own auth widget in
            production - not a route in this app. */}
      </Routes>
    </Router>
  );
}

export default App;